import { useBookingLocale } from '../../hooks/useBookingLocale';
import { currencyCodeSuffix } from '../../utils/money';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { BookingStatus, ErrorCode } from '../../../__generated__/graphql';
import { Button } from '../../components/button';
import { Modal } from '../../components/modal';
import {
  Alert,
  Badge,
  Divider,
  LoadingBlock,
  Muted,
  Stack,
  Strong,
  Textarea,
} from '../../components/ui';
import {
  useAcceptBookingRequestMutation,
  useDeclineBookingRequestMutation,
  useStayBookingAsHostQuery,
} from '../../services/stayService/__generated__/queries.generated';
import { firstGraphQLError, stayErrorMessage } from '../StayHostPage/stayErrors';
import { formatMoney } from '../StayGuestPage/stayQuoteLines';
import {
  CancelStayModal,
  Columns,
  Detail,
  Page,
  PolicyText,
  Section,
  SectionHeading,
  SideCard,
  StayBookingSummary,
  Title,
  bookingSubtitle,
} from './StayBookingComponents';
import { guestCountsLabel, hostPayout, stayDateTime, stayStatusView } from './stayBooking';
import { useOpenBookingChat } from './useOpenBookingChat';
import {
  CheckInDetailsSection,
  StayRequestSection,
  StayReviewSection,
} from './StayLifecycleSections';

/**
 * A reservation from the host's side (spec 016 W19): answer a request (time left,
 * the guest's message), see the payout, cancel a confirmed stay (D6), check what the
 * guest gets at check-in, then review the guest and open a Resolution Center request.
 */
export default function StayReservationPage() {
  const { bookingId = '' } = useParams<{ bookingId: string }>();
  const tripLocale = useBookingLocale(bookingId, 'host');
  const navigate = useNavigate();
  const [declineOpen, setDeclineOpen] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [cancelOpen, setCancelOpen] = useState(false);
  const chat = useOpenBookingChat();
  const { data, loading, error, refetch } = useStayBookingAsHostQuery({
    variables: { id: bookingId },
    skip: !bookingId,
    fetchPolicy: 'cache-and-network',
  });
  const [accept, { loading: accepting }] = useAcceptBookingRequestMutation();
  const [decline, { loading: declining }] = useDeclineBookingRequestMutation();
  const booking = data?.getBookingAsHost;

  if (loading && !booking) return <LoadingBlock label="Loading reservation" />;
  if (!booking) {
    return (
      <Page>
        <Alert tone="error" title="Reservation not found">
          {error ? stayErrorMessage(error) : 'This reservation does not exist.'}
        </Alert>
      </Page>
    );
  }

  const zone = booking.car?.location?.timezone;
  const view = stayStatusView(booking, 'host');
  const guestName = booking.guest?.firstName || booking.guest?.displayName || 'The guest';
  const payout = hostPayout(booking.receipt);
  const canCancel = booking.status === BookingStatus.Booked && Date.now() < booking.endAt;

  const acceptRequest = async () => {
    try {
      const { data: answer } = await accept({ variables: { bookingId } });
      if (answer?.acceptBookingRequest.status === BookingStatus.Booked) {
        toast.success('Request accepted. The stay is booked.');
      } else {
        toast.error('The guest’s card was declined, so the stay was not booked.');
      }
    } catch (e) {
      const code = firstGraphQLError(e)?.extensions?.code;
      toast.error(stayErrorMessage(e, 'The request could not be accepted'), { autoClose: 8000 });
      // A conflict or expiry closed the request on the server: show its new status.
      if (code === ErrorCode.RequestConflict || code === ErrorCode.RequestExpired) void refetch();
    }
  };

  const declineRequest = async () => {
    try {
      await decline({ variables: { bookingId, reason: declineReason.trim() || null } });
      setDeclineOpen(false);
      toast.success('Request declined. The hold on the guest’s card was released.');
    } catch (e) {
      toast.error(stayErrorMessage(e, 'The request could not be declined'));
    }
  };

  return (
    <Page data-testid="stay-reservation-page">
      <Muted $size="sm">
        <Link to="/stays/host/reservations">Reservations</Link>
      </Muted>
      <Title>
        {guestName} · {booking.car?.name || 'Your place'}
      </Title>
      <Stack $gap={4} style={{ marginTop: 8 }}>
        <div>
          <Badge tone={view.tone} data-testid="stay-reservation-status">
            {view.label}
          </Badge>
        </div>
        <Muted>{view.description}</Muted>
      </Stack>
      <Columns>
        <div>
          {booking.status === BookingStatus.Requested && (
            <Section>
              <SectionHeading>{guestName} wants to stay</SectionHeading>
              <Stack $gap={4}>
                <Muted data-testid="stay-request-message">
                  {booking.requestMessage || 'No message.'}
                </Muted>
                <Muted $size="sm">
                  Accepting charges the guest’s card now. Declining releases the hold. Dates booked
                  by someone else in the meantime cannot be accepted.
                </Muted>
                <Stack $gap={4}>
                  <Button
                    text="Accept"
                    size="lg"
                    fullWidth
                    isLoading={accepting}
                    onClick={acceptRequest}
                    dataTestId="stay-accept"
                  />
                  <Button
                    theme="outlined"
                    text="Decline"
                    fullWidth
                    onClick={() => setDeclineOpen(true)}
                    dataTestId="stay-decline"
                  />
                </Stack>
              </Stack>
            </Section>
          )}

          <Section>
            <SectionHeading>Stay details</SectionHeading>
            <Stack $gap={4}>
              <Detail label="Check-in">{stayDateTime(booking.startAt, zone)}</Detail>
              <Detail label="Checkout">{stayDateTime(booking.endAt, zone)}</Detail>
              <Detail label="Guests">{guestCountsLabel(booking.guestCounts)}</Detail>
              {booking.shortId ? (
                <Detail label="Confirmation code">{booking.shortId}</Detail>
              ) : null}
            </Stack>
          </Section>

          <StayReviewSection booking={booking} viewer="host" />
          <CheckInDetailsSection booking={booking} viewer="host" />

          {booking.status !== BookingStatus.Requested && booking.requestMessage ? (
            <Section>
              <SectionHeading>Message from {guestName}</SectionHeading>
              <Muted>{booking.requestMessage}</Muted>
            </Section>
          ) : null}

          <Section>
            <SectionHeading>Cancellation policy</SectionHeading>
            <PolicyText policy={booking.cancellationPolicySnapshot} />
          </Section>

          <StayRequestSection booking={booking} viewer="host" />

          <Section>
            <Stack $gap={4}>
              {canCancel && (
                <Button
                  theme="outlined"
                  text="Cancel reservation"
                  onClick={() => setCancelOpen(true)}
                  dataTestId="stay-host-cancel"
                />
              )}
              <Button
                theme="text"
                text="Message the guest"
                isLoading={chat.loading}
                onClick={() => chat.open(booking)}
                dataTestId="stay-message-guest"
              />
            </Stack>
          </Section>
        </div>

        <SideCard>
          <Stack $gap={4}>
            <StayBookingSummary
              name={booking.car?.name}
              imageUrl={booking.car?.images?.[0]?.imageUrl}
              subtitle={bookingSubtitle(booking)}
            />
            <Stack $gap={4} data-testid="stay-host-payout">
              <Strong>Your earnings</Strong>
              <Row label="Nights, cleaning and guest fees" value={payout.subtotal} />
              <Row label="Host service fee" value={-payout.hostServiceFee} />
              <Divider />
              <Row label="You earn" value={payout.payout} strong currency={tripLocale.currency} />
              <Muted $size="sm">
                The guest also pays a service fee and taxes, which are not part of your payout.
              </Muted>
            </Stack>
          </Stack>
        </SideCard>
      </Columns>

      <Modal
        isOpen={declineOpen}
        onClose={() => setDeclineOpen(false)}
        title="Decline request"
        modalWidth="440px"
      >
        <Stack $gap={4}>
          <Strong style={{ fontSize: 20 }}>Decline this request?</Strong>
          <Muted>The hold on the guest’s card is released right away.</Muted>
          <Textarea
            rows={3}
            maxLength={500}
            placeholder="Reason (optional, the guest does not see it)"
            value={declineReason}
            onChange={e => setDeclineReason(e.target.value)}
          />
          <Button
            theme="danger"
            text="Decline"
            fullWidth
            isLoading={declining}
            onClick={declineRequest}
            dataTestId="stay-decline-confirm"
          />
        </Stack>
      </Modal>
      <CancelStayModal
        bookingId={booking.id}
        isOpen={cancelOpen}
        onClose={() => setCancelOpen(false)}
        onCancelled={() => {
          setCancelOpen(false);
          void refetch();
        }}
      />
    </Page>
  );
}

function Row({
  label,
  value,
  strong,
  currency,
}: {
  label: string;
  value: number;
  strong?: boolean;
  /** Spec 028 D14: the payout total carries the code for CAD. */
  currency?: string;
}) {
  const base = value < 0 ? `-${formatMoney(-value)}` : formatMoney(value);
  const text = strong ? `${base}${currencyCodeSuffix(currency)}` : base;
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14 }}>
      {strong ? <Strong>{label}</Strong> : <span>{label}</span>}
      {strong ? <Strong>{text}</Strong> : <span>{text}</span>}
    </div>
  );
}
